/**
 * StorageAdapter — Abstraction Layer for persistent game data.
 * 
 * Isolates the game engine from raw localStorage calls.
 * - Development: Persists to window.localStorage with an in-memory fallback (for Incognito/Node.js tests).
 * - Production: Can be seamlessly swapped or augmented with cloud/REST sync without touching game logic.
 */

class MemoryStore {
  constructor() {
    this._data = new Map();
  }
  get length() {
    return this._data.size;
  }
  key(index) {
    return Array.from(this._data.keys())[index] ?? null;
  }
  getItem(key) {
    return this._data.has(key) ? this._data.get(key) : null;
  }
  setItem(key, value) {
    this._data.set(key, String(value));
  }
  removeItem(key) {
    this._data.delete(key);
  }
  clear() {
    this._data.clear();
  }
}

export const RELEASE_WIPE_VERSION = '2026-09-18_v2';
export const RELEASE_WIPE_KEY = 'ucg_release_wipe_v2';

export const PRESERVED_SETTINGS_KEYS = new Set([
  'ucg_sound_muted',
  'ucg_music_volume',
  'ucg_sfx_volume',
  'ucg_music_enabled',
  'ucg_sprites_hd',
  'ucg_sprites_uhd',
  'ucg_quality_manual',
  'ucg_ai_difficulty',
  'ucg_account_id',
  'ucg_account_secret',
  'ucg_player_name',
  'ucg_save_version',
  'void_alpha_access',
  RELEASE_WIPE_KEY
]);

export function performReleaseWipeIfNeeded(store = (typeof window !== 'undefined' ? window.localStorage : null)) {
  if (!store || typeof store.getItem !== 'function') return false;
  try {
    if (store.getItem(RELEASE_WIPE_KEY) === RELEASE_WIPE_VERSION) {
      return false;
    }

    const toRemove = [];
    const count = typeof store.length === 'number' ? store.length : 0;
    for (let i = 0; i < count; i++) {
      const k = typeof store.key === 'function' ? store.key(i) : null;
      if (!k) continue;

      // Wipe EVERYTHING (decks, cards, wallets, progress, accounts) except audio/graphics preferences
      if (!PRESERVED_SETTINGS_KEYS.has(k)) {
        toRemove.push(k);
      }
    }

    for (const key of toRemove) {
      store.removeItem(key);
    }

    store.setItem(RELEASE_WIPE_KEY, RELEASE_WIPE_VERSION);
    return true;
  } catch (err) {
    console.warn('[StorageAdapter] Release wipe failed:', err);
    return false;
  }
}

class StorageAdapter {
  constructor(namespace = 'ucg') {
    this.namespace = namespace;
    this._store = this._resolveBackendStore();
    performReleaseWipeIfNeeded(this._store);
  }

  _resolveBackendStore() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const testKey = '__ucg_test_storage__';
        window.localStorage.setItem(testKey, '1');
        window.localStorage.removeItem(testKey);
        return window.localStorage;
      }
    } catch {
      // localStorage is blocked (Private Mode, iframe, or quota)
    }
    return new MemoryStore();
  }

  _formatKey(key) {
    return `${this.namespace}:${key}`;
  }

  get(key, defaultValue = null) {
    try {
      const raw = this._store.getItem(this._formatKey(key));
      if (raw === null || raw === undefined) {
        return defaultValue;
      }
      return JSON.parse(raw);
    } catch (err) {
      console.warn(`[StorageAdapter] Failed to read key "${key}":`, err);
      return defaultValue;
    }
  }

  set(key, value) {
    try {
      const serialized = JSON.stringify(value);
      this._store.setItem(this._formatKey(key), serialized);
      return true;
    } catch (err) {
      console.warn(`[StorageAdapter] Failed to write key "${key}":`, err);
      return false;
    }
  }

  remove(key) {
    try {
      this._store.removeItem(this._formatKey(key));
      return true;
    } catch (err) {
      console.warn(`[StorageAdapter] Failed to remove key "${key}":`, err);
      return false;
    }
  }

  has(key) {
    try {
      return this._store.getItem(this._formatKey(key)) !== null;
    } catch {
      return false;
    }
  }
}

// Global shared singleton instance
export const storageAdapter = new StorageAdapter('ucg');
export { StorageAdapter };
